import { type FC, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { X } from 'lucide-react'

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/Select'
import { AdminHeader } from '@/components/organisms/AdminHeader'
import { StatsChart } from '@/components/organisms/StatsChart'
import {
  type AvailableDate,
  type AvailableRegion,
  type ChartDataPoint,
  type CurrentCampaignDate,
  FIXED_DATE_OPTIONS,
  type SelectedDate,
  type SelectionMode,
  WEEKDAY_OPTIONS,
} from '@/pages/stats/useStatsPage.ts'

export type StatsSection = 'requests' | 'configuration'

export interface StatsViewProps {
  availableRegions: AvailableRegion[]
  availableDates: AvailableDate[]
  currentCampaignDates: CurrentCampaignDate[]
  selectedRegionId: string | undefined
  selectedMode: SelectionMode
  selectedDates: SelectedDate[]
  selectedFixedDay: number | undefined
  selectedWeekday: number | undefined
  selectedReferenceDate: string | undefined
  chartData: ChartDataPoint[]
  isLoading: boolean // True only on initial load (no cached data)
  isFetching: boolean // True when fetching, even if we have stale/cached data
  selectedSection: StatsSection
  onSectionChange: (section: StatsSection) => void
  onRegionChange: (regionId: string | undefined) => void
  onModeChange: (mode: SelectionMode) => void
  onDateToggle: (date: string) => void
  onClearDates: () => void
  onFixedDayChange: (day: number | undefined) => void
  onWeekdayChange: (day: number | undefined) => void
  onReferenceDateChange: (date: string | undefined) => void
  onBackToPiloteClicked: () => void
}

export const StatsView: FC<StatsViewProps> = ({
  availableRegions,
  availableDates,
  currentCampaignDates,
  selectedRegionId,
  selectedMode,
  selectedDates,
  selectedFixedDay,
  selectedWeekday,
  selectedReferenceDate,
  chartData,
  isLoading,
  isFetching,
  selectedSection,
  onSectionChange,
  onRegionChange,
  onModeChange,
  onDateToggle,
  onClearDates,
  onFixedDayChange,
  onWeekdayChange,
  onReferenceDateChange,
  onBackToPiloteClicked,
}) => {
  const intl = useIntl()

  // Filter available dates that are not already selected (for manual mode)
  const filteredDates = useMemo(() => {
    const selectedDateStrings = new Set(selectedDates.map((d) => d.date))
    return availableDates.filter((d) => !selectedDateStrings.has(d.date))
  }, [availableDates, selectedDates])

  // Get selected region name for display
  const selectedRegionName = useMemo(() => {
    if (!selectedRegionId) {
      return intl.formatMessage({ id: 'stats.allRegions' })
    }
    return availableRegions.find((r) => r.regionId === selectedRegionId)?.regionName ?? selectedRegionId
  }, [selectedRegionId, availableRegions, intl])

  // Get mode label for display
  const selectedModeName = useMemo(() => intl.formatMessage({ id: `stats.mode.${selectedMode}` }), [selectedMode, intl])

  return (
    <div className="flex h-full flex-col">
      <AdminHeader
        title={intl.formatMessage({ id: 'stats.title' })}
        titleClassName="text-blue-300"
        onBackToPiloteClicked={onBackToPiloteClicked}
      />

      <div className="flex-1 overflow-auto p-4 pl-16">
        {/* Section Tabs */}
        <div className="mb-6 flex gap-2">
          <button
            type="button"
            onClick={() => {
              onSectionChange('requests')
            }}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
              selectedSection === 'requests' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}>
            <FormattedMessage id="stats.section.requests" />
          </button>
          <button
            type="button"
            onClick={() => {
              onSectionChange('configuration')
            }}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
              selectedSection === 'configuration'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}>
            <FormattedMessage id="stats.section.configuration" />
          </button>
        </div>

        {/* Selection Controls */}
        <div className="mb-6 flex flex-wrap items-start gap-4">
          {/* Region Dropdown */}
          <div className="w-64">
            <label className="mb-1 block text-sm font-medium text-gray-700">
              <FormattedMessage id="stats.region" />
            </label>
            <Select
              value={selectedRegionId ?? 'all'}
              onValueChange={(value) => {
                onRegionChange(value === 'all' ? undefined : value)
              }}>
              <SelectTrigger aria-label={intl.formatMessage({ id: 'stats.region' })}>
                <SelectValue>{selectedRegionName}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  <FormattedMessage id="stats.allRegions" />
                </SelectItem>
                {availableRegions.map((region) => (
                  <SelectItem key={region.regionId} value={region.regionId}>
                    {region.regionName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Mode Dropdown */}
          <div className="w-56">
            <label className="mb-1 block text-sm font-medium text-gray-700">
              <FormattedMessage id="stats.mode" />
            </label>
            <Select
              value={selectedMode}
              onValueChange={(value) => {
                onModeChange(value as SelectionMode)
              }}>
              <SelectTrigger aria-label={intl.formatMessage({ id: 'stats.mode' })}>
                <SelectValue>{selectedModeName}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>
                    <FormattedMessage id="stats.mode.group.campaign" />
                  </SelectLabel>
                  <SelectItem value="manual">
                    <FormattedMessage id="stats.mode.manual" />
                  </SelectItem>
                  <SelectItem value="weekday">
                    <FormattedMessage id="stats.mode.weekday" />
                  </SelectItem>
                </SelectGroup>
                <SelectGroup>
                  <SelectLabel>
                    <FormattedMessage id="stats.mode.group.yearOverYear" />
                  </SelectLabel>
                  <SelectItem value="isoWeek">
                    <FormattedMessage id="stats.mode.isoWeek" />
                  </SelectItem>
                  <SelectItem value="fixedDate">
                    <FormattedMessage id="stats.mode.fixedDate" />
                  </SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          {/* Mode-specific selection */}
          {selectedMode === 'manual' && (
            <div className="w-80">
              <label className="mb-1 block text-sm font-medium text-gray-700">
                <FormattedMessage id="stats.addDate" />
              </label>
              <Select
                value=""
                onValueChange={(value) => {
                  onDateToggle(value)
                }}>
                <SelectTrigger aria-label={intl.formatMessage({ id: 'stats.addDate' })}>
                  <SelectValue placeholder={intl.formatMessage({ id: 'stats.selectDate' })} />
                </SelectTrigger>
                <SelectContent>
                  {filteredDates.map((date) => (
                    <SelectItem key={date.date} value={date.date}>
                      {date.label}
                    </SelectItem>
                  ))}
                  {filteredDates.length === 0 && (
                    <div className="px-4 py-2 text-gray-500">
                      <FormattedMessage id="stats.noDate" />
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>
          )}

          {selectedMode === 'fixedDate' && (
            <div className="w-56">
              <label className="mb-1 block text-sm font-medium text-gray-700">
                <FormattedMessage id="stats.fixedDate.label" />
              </label>
              <Select
                value={selectedFixedDay?.toString() ?? ''}
                onValueChange={(value) => {
                  onFixedDayChange(value ? Number(value) : undefined)
                }}>
                <SelectTrigger aria-label={intl.formatMessage({ id: 'stats.fixedDate.label' })}>
                  <SelectValue placeholder={intl.formatMessage({ id: 'stats.fixedDate.select' })} />
                </SelectTrigger>
                <SelectContent>
                  {FIXED_DATE_OPTIONS.map((option) => (
                    <SelectItem key={option.day} value={option.day.toString()}>
                      <FormattedMessage id={option.labelKey} />
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {selectedMode === 'weekday' && (
            <div className="w-56">
              <label className="mb-1 block text-sm font-medium text-gray-700">
                <FormattedMessage id="stats.weekday.label" />
              </label>
              <Select
                value={selectedWeekday?.toString() ?? ''}
                onValueChange={(value) => {
                  onWeekdayChange(value ? Number(value) : undefined)
                }}>
                <SelectTrigger aria-label={intl.formatMessage({ id: 'stats.weekday.label' })}>
                  <SelectValue placeholder={intl.formatMessage({ id: 'stats.weekday.select' })} />
                </SelectTrigger>
                <SelectContent>
                  {WEEKDAY_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value.toString()}>
                      <FormattedMessage id={option.labelKey} />
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {selectedMode === 'isoWeek' && (
            <div className="w-80">
              <label className="mb-1 block text-sm font-medium text-gray-700">
                <FormattedMessage id="stats.isoWeek.label" />
              </label>
              <Select
                value={selectedReferenceDate ?? ''}
                onValueChange={(value) => {
                  onReferenceDateChange(value || undefined)
                }}>
                <SelectTrigger aria-label={intl.formatMessage({ id: 'stats.isoWeek.label' })}>
                  <SelectValue placeholder={intl.formatMessage({ id: 'stats.isoWeek.select' })} />
                </SelectTrigger>
                <SelectContent>
                  {currentCampaignDates.map((date) => (
                    <SelectItem key={date.date} value={date.date}>
                      {date.label}
                    </SelectItem>
                  ))}
                  {currentCampaignDates.length === 0 && (
                    <div className="px-4 py-2 text-gray-500">
                      <FormattedMessage id="stats.isoWeek.noDate" />
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Selected Date Pills (for manual mode) */}
          {selectedMode === 'manual' && (
            <div className="flex flex-1 flex-wrap items-end gap-2 pt-6">
              {selectedDates.map((date) => (
                <div
                  key={date.date}
                  className="flex items-center gap-2 rounded-full px-3 py-1 text-sm text-white"
                  style={{ backgroundColor: date.color }}>
                  <span>{date.label}</span>
                  <button
                    type="button"
                    onClick={() => {
                      onDateToggle(date.date)
                    }}
                    className="hover:opacity-80">
                    <X className="size-4" />
                  </button>
                </div>
              ))}
              {selectedDates.length > 1 && (
                <button
                  type="button"
                  onClick={onClearDates}
                  className="rounded-full border border-gray-300 px-3 py-1 text-sm text-gray-600 hover:bg-gray-100">
                  <FormattedMessage id="stats.clearAll" />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Legend for non-manual modes */}
        {selectedMode !== 'manual' && selectedDates.length > 0 && (
          <div className="mb-4 flex flex-wrap gap-3">
            {selectedDates.map((date) => (
              <div key={date.date} className="flex items-center gap-2">
                <div className="size-3 rounded-full" style={{ backgroundColor: date.color }} />
                <span className="text-sm text-gray-700">{date.label}</span>
              </div>
            ))}
          </div>
        )}

        {/* Chart */}
        <div className="relative h-[600px] w-full rounded-lg border border-gray-200 bg-white p-4">
          {/* Loading overlay - shows over existing chart while fetching new data */}
          {isFetching && chartData.length > 0 && (
            <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/70">
              <svg className="size-8 animate-spin text-blue-600" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            </div>
          )}

          {/* Initial loading state - no data yet */}
          {isLoading && chartData.length === 0 ? (
            <div className="flex h-full items-center justify-center text-gray-500">
              <FormattedMessage id="stats.loading" />
            </div>
          ) : selectedDates.length === 0 ? (
            <div className="flex h-full items-center justify-center text-gray-500">
              <FormattedMessage id="stats.selectDate" />
            </div>
          ) : (
            <StatsChart selectedDates={selectedDates} chartData={chartData} height={560} />
          )}
        </div>
      </div>
    </div>
  )
}
